import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { useTitle } from "../../../hooks/useTitle";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import AppPage from "../../../components/CustomUI/AppPage";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import CardContainer from "../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import Button from "../../../components/BaseUI/Button";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import Lozenge from "../../../components/BaseUI/Lozenge";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiRefreshCw,
  FiCalendar,
  FiCoffee,
  FiChevronDown,
  FiChevronUp,
  FiX,
} from "react-icons/fi";
import { withAuth } from "../../../components/HOC/withAuth";
import "./Shifts.scss";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const DAYS = [
  { key: "monday", label: "Pzt" },
  { key: "tuesday", label: "Sal" },
  { key: "wednesday", label: "Çar" },
  { key: "thursday", label: "Per" },
  { key: "friday", label: "Cum" },
  { key: "saturday", label: "Cmt" },
  { key: "sunday", label: "Paz" },
];

const DAY_FULL = {
  monday: "Pazartesi",
  tuesday: "Salı",
  wednesday: "Çarşamba",
  thursday: "Perşembe",
  friday: "Cuma",
  saturday: "Cumartesi",
  sunday: "Pazar",
};

const tableColumns = [
  {
    name: "Vardiya Adı",
    dataType: "text",
    id: 0,
    customSorter: (a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || "")),
  },
  {
    name: "Saat özeti",
    dataType: "text",
    id: 1,
    customSorter: (a, b) => {
      const ah = formatHoursSummary(a);
      const bh = formatHoursSummary(b);
      return String(ah.sortKey || "").localeCompare(String(bh.sortKey || ""));
    },
  },
  {
    name: "Günler",
    dataType: "number",
    id: 2,
    customSorter: (a, b) =>
      Number((a?.daySchedules || []).length) -
      Number((b?.daySchedules || []).length),
  },
  {
    name: "Molalar",
    dataType: "number",
    id: 3,
    customSorter: (a, b) => {
      const count = (s) =>
        (s?.daySchedules || []).reduce(
          (acc, d) => acc + Number((d?.breaks || []).length || 0),
          0,
        );
      return count(a) - count(b);
    },
  },
  {
    name: "Tolerans",
    dataType: "number",
    id: 4,
    customSorter: (a, b) =>
      clampInt(a?.toleranceMinutes ?? 0, 0, 1440) -
      clampInt(b?.toleranceMinutes ?? 0, 0, 1440),
  },
  {
    name: "Durum",
    dataType: "text",
    id: 5,
    customSorter: (a, b) => Number(!!b?.isActive) - Number(!!a?.isActive),
  },
  { name: "İşlemler", id: 6, sortable: false },
];

const toastAppearance = (type) => {
  if (type === "error") return "danger";
  return type || "success";
};

const getDayLabel = (key) => DAYS.find((d) => d.key === key)?.label || key;

const formatHoursSummary = (shift) => {
  const ds = shift.daySchedules || [];
  if (!ds.length) return { lines: ["—"], title: "—", sortKey: "—" };
  const slotKey = (s) => `${s.startTime}|${s.endTime}`;
  const keys = Array.from(new Set(ds.map(slotKey)));
  if (keys.length === 1) {
    const line = `${formatTimeRangeShort(ds[0].startTime, ds[0].endTime)}`;
    return { lines: [line], title: line, sortKey: line };
  }

  const groups = keys
    .map((k) => {
      const [st, et] = String(k).split("|");
      const days = ds
        .filter((x) => slotKey(x) === k)
        .map((x) => getDayLabel(x.day));
      return {
        key: k,
        range: formatTimeRangeShort(st, et),
        days,
      };
    })
    .filter((g) => g.range);

  const fmtDaysCompact = (days) => {
    const d = Array.isArray(days) ? days : [];
    if (d.length === 0) return "";
    const shownDays = d.slice(0, 3);
    const more = Math.max(0, d.length - shownDays.length);
    return `${shownDays.join(",")}${more ? `+${more}` : ""}`;
  };

  const fmtDaysFull = (days) => {
    const d = Array.isArray(days) ? days : [];
    if (d.length === 0) return "";
    return d.join(",");
  };

  const allLines = groups.map((g) => `${g.range} (${fmtDaysCompact(g.days)})`);
  const allLinesFull = groups.map((g) => `${g.range} (${fmtDaysFull(g.days)})`);
  const maxLines = 3;
  const shownLines = allLines.slice(0, maxLines);
  const hidden = Math.max(0, allLines.length - shownLines.length);
  const lines = hidden ? [...shownLines, `+${hidden}`] : shownLines;

  return {
    lines,
    title: allLinesFull.join("\n"),
    sortKey: allLines.join(" | "),
  };
};

const formatBreaksSummary = (shift) => {
  const ds = shift.daySchedules || [];
  let total = 0;
  for (const s of ds) {
    const n = (s.breaks || []).length;
    total += n;
  }
  if (total === 0) return { main: "—", sub: null };
  return {
    main: `${total} mola`,
    sub: null,
  };
};

const clampInt = (v, min, max) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, Math.trunc(n)));
};

const formatTimeShort = (t) => {
  const s = String(t || "").trim();
  if (!s) return "—";
  // 08:00 -> 08, 08:30 -> 08:30
  return s.endsWith(":00") ? s.slice(0, 2) : s;
};

const formatTimeRangeShort = (start, end) =>
  `${formatTimeShort(start)}–${formatTimeShort(end)}`;

const ellipsize = (s, maxLen = 80) => {
  const t = String(s || "");
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen - 1)}…`;
};

const Shifts = () => {
  useTitle("Rovion MES - Vardiyalar");
  const navigate = useNavigate();
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/shifts",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );

  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    shiftId: null,
  });
  const [detailsModal, setDetailsModal] = useState({
    isOpen: false,
    shift: null,
  });
  const [openBreakDays, setOpenBreakDays] = useState(() => new Set());

  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("shifts-list", tableColumns);

  const dismissToast = useCallback((toastId) => {
    setToasts((prev) => prev.filter((t) => t.id !== toastId));
  }, []);

  const addToast = useCallback((message, type = "success") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
  }, []);

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );

  const fetchShifts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/shifts`, authHeaders());
      setShifts(data.shifts || []);
    } catch {
      addToast("Vardiyalar yüklenemedi", "danger");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, addToast]);

  useEffect(() => {
    fetchShifts();
  }, [fetchShifts]);

  const handleDelete = async (delId) => {
    try {
      await axios.delete(`${API_URL}/api/shifts/${delId}`, authHeaders());
      addToast("Vardiya silindi", "success");
      fetchShifts();
    } catch (err) {
      addToast(err?.response?.data?.message || "Silme başarısız", "danger");
    } finally {
      setConfirmModal({ isOpen: false, shiftId: null });
    }
  };

  return (
    <AppPage
      title="Vardiyalar"
      description="Her gün için ayrı çalışma saatleri ve molalar tanımlayın"
      actions={
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {canWrite && (
            <Button
              appearance="primary-animated"
              onClick={() => navigate("/shifts/create")}
              icon={<FiPlus />}
            >
              Yeni Vardiya
            </Button>
          )}
        </div>
      }
    >
      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.message}
            appearance={toastAppearance(t.type)}
            onClose={() => dismissToast(t.id)}
          />
        ))}
      </ToastContainer>

      <Modal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, shiftId: null })}
      >
        <ModalHeader>Vardiyayı Sil</ModalHeader>
        <ModalBody>Bu vardiyayı silmek istediğinize emin misiniz?</ModalBody>
        <ModalFooter>
          <Button
            appearance="subtle"
            onClick={() => setConfirmModal({ isOpen: false, shiftId: null })}
          >
            İptal
          </Button>
          <Button
            appearance="danger"
            onClick={() => handleDelete(confirmModal.shiftId)}
          >
            Sil
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={detailsModal.isOpen} width="medium">
        <ModalBody>
          <div className="d-flex justify-content-between align-items-center mb-3 mt-1">
            <h3 className="mb-0">
              {detailsModal.shift?.name || "Vardiya"} Vardiya Detayı
            </h3>
            <Button
              appearance="danger-subtle"
              onClick={() => setDetailsModal({ isOpen: false, shift: null })}
            >
              <FiX size={16} />
            </Button>
          </div>
          {detailsModal.shift ? (
            <div className="d-flex flex-column gap-3">
              {(detailsModal.shift.daySchedules || []).every(
                (s) => !(s.breaks || []).length,
              ) ? (
                <div className="text-muted">Mola yok</div>
              ) : (
                <div className="shifts-details-grid">
                  {(detailsModal.shift.daySchedules || []).map((s) => (
                    <div key={`br-${s.day}`} className="shifts-details-card">
                      <div className="shifts-details-card__head">
                        <div className="shifts-details-card__title">
                          {DAY_FULL[s.day] || s.day}
                        </div>
                        <div className="shifts-details-card__meta">
                          <div className="shifts-details-card__time">
                            {formatTimeRangeShort(s.startTime, s.endTime)}
                          </div>
                          <button
                            type="button"
                            className="shifts-details-breaks-toggle"
                            onClick={() =>
                              setOpenBreakDays((prev) => {
                                const next = new Set(prev);
                                if (next.has(s.day)) next.delete(s.day);
                                else next.add(s.day);
                                return next;
                              })
                            }
                            title="Molaları aç/kapat"
                          >
                            <span className="small text-muted">Molalar:</span>
                            <Lozenge appearance="default-subtle" isBold>
                              {(s.breaks || []).length}
                            </Lozenge>
                            {openBreakDays.has(s.day) ? (
                              <FiChevronUp size={16} />
                            ) : (
                              <FiChevronDown size={16} />
                            )}
                          </button>
                        </div>
                      </div>
                      {openBreakDays.has(s.day) &&
                        (s.breaks || []).length > 0 && (
                          <div className="shifts-details-breaks">
                            {(s.breaks || []).map((b, idx) => (
                              <div
                                key={`${s.day}-${idx}`}
                                className="shifts-details-break"
                              >
                                <div className="shifts-details-break__row">
                                  <div className="shifts-details-break__name">
                                    {b.name}
                                  </div>
                                  <div className="shifts-details-break__time">
                                    {formatTimeRangeShort(
                                      b.startTime,
                                      b.endTime,
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="text-muted">—</div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            appearance="primary-animated"
            onClick={() => setDetailsModal({ isOpen: false, shift: null })}
          >
            Kapat
          </Button>
        </ModalFooter>
      </Modal>

      <CardContainer>
        <Table
          columns={tableColumns}
          visibleColumns={visibleColumns}
          tableId="shifts-list"
          loading={loading}
          search={true}
          viewOptions={
            <ListViewOptionsPanel
              columns={tableColumns}
              visibleColumns={visibleColumns}
              onToggleColumn={toggleColumnVisibility}
              onResetColumns={resetToDefault}
              showApply={false}
              showReset={false}
            />
          }
        >
          {shifts.map((shift) => {
            const hours = formatHoursSummary(shift);
            const br = formatBreaksSummary(shift);
            const dayKeys = (shift.daySchedules || []).map((s) => s.day);
            const tol = clampInt(shift.toleranceMinutes ?? 0, 0, 1440);
            // days/breaks are shown as counts; details are in modal on click
            return (
              <Tr key={shift._id} data={shift}>
                {visibleColumns.includes(0) && (
                <Td>
                  <div
                    style={{
                      maxWidth: 280,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={shift.name}
                  >
                    <strong>{shift.name}</strong>
                  </div>
                </Td>
                )}
                {visibleColumns.includes(1) && (
                <Td>
                  <div className="shifts-hours-cell" title={hours.title}>
                    <div
                      className="shifts-hours-summary shifts-hours-cell__preview"
                      style={{
                        maxWidth: 220,
                        whiteSpace: "normal",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {hours.lines.map((ln, i) => (
                        <div key={`h-${shift._id}-${i}`}>{ln}</div>
                      ))}
                    </div>
                  </div>
                </Td>
                )}
                {visibleColumns.includes(2) && (
                <Td>
                  {dayKeys.length > 0 ? (
                    <Button
                      appearance="primary-subtle"
                      className="shifts-cell-chip"
                      title={`Günler: ${dayKeys.length}`}
                      onClick={() => {
                        setOpenBreakDays(new Set());
                        setDetailsModal({ isOpen: true, shift });
                      }}
                    >
                      <span className="shifts-cell-chip__inner">
                        <FiCalendar size={14} />
                        <span className="shifts-cell-chip__count">
                          {dayKeys.length}
                        </span>
                      </span>
                    </Button>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </Td>
                )}
                {visibleColumns.includes(3) && (
                <Td>
                  {br.main === "—" ? (
                    <span className="text-muted">—</span>
                  ) : (
                    <Button
                      appearance="primary-subtle"
                      className="shifts-cell-chip"
                      title={`${br.main}`}
                      onClick={() => {
                        setOpenBreakDays(new Set());
                        setDetailsModal({ isOpen: true, shift });
                      }}
                    >
                      <span className="shifts-cell-chip__inner">
                        <FiCoffee size={14} />
                        <span className="shifts-cell-chip__count">
                          {clampInt(String(br.main).split(" ")[0], 0, 9999)}
                        </span>
                      </span>
                    </Button>
                  )}
                </Td>
                )}
                {visibleColumns.includes(4) && (
                <Td>
                  <Lozenge
                    appearance={tol > 0 ? "warning-subtle" : "default-subtle"}
                    isBold
                  >
                    {tol} dk
                  </Lozenge>
                </Td>
                )}
                {visibleColumns.includes(5) && (
                <Td>
                  {shift.isActive ? (
                    <Lozenge appearance="success-subtle" isBold>
                      Aktif
                    </Lozenge>
                  ) : (
                    <Lozenge appearance="default-subtle" isBold>
                      Pasif
                    </Lozenge>
                  )}
                </Td>
                )}
                {visibleColumns.includes(6) && (
                <Td>
                  <div className="d-flex align-items-center">
                    {canWrite && (
                      <>
                        <IconButton
                          icon={<FiEdit2 />}
                          label="Düzenle"
                          appearance="subtle"
                          shape="circle"
                          onClick={() => navigate(`/shifts/edit/${shift._id}`)}
                        />
                        <IconButton
                          icon={<FiTrash2 />}
                          label="Sil"
                          appearance="subtle"
                          shape="circle"
                          onClick={() =>
                            setConfirmModal({
                              isOpen: true,
                              shiftId: shift._id,
                            })
                          }
                        />
                      </>
                    )}
                  </div>
                </Td>
                )}
              </Tr>
            );
          })}
        </Table>
      </CardContainer>
    </AppPage>
  );
};

export default withAuth(Shifts);
